<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click="handleOverlayClick">
      <div class="modal-content" :class="sizeClass" @click.stop>
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <button type="button" class="btn-close" @click="close" v-if="showCloseButton">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <p v-if="message" class="mb-3">{{ message }}</p>

          <!-- Input for prompt -->
          <div v-if="type === 'prompt'" class="mb-3">
            <label :for="'modal-input-' + id" class="form-label">{{ inputLabel }}</label>
            <input
              :id="'modal-input-' + id"
              v-model="localInputValue"
              type="text"
              class="form-control"
              :placeholder="inputPlaceholder"
              @keyup.enter="handleConfirm"
              ref="inputRef"
            />
          </div>

          <!-- Select for status change -->
          <div v-if="type === 'select'" class="mb-3">
            <label :for="'modal-select-' + id" class="form-label">{{ selectLabel }}</label>
            <select
              :id="'modal-select-' + id"
              v-model="localSelectedValue"
              class="form-select"
              ref="selectRef"
            >
              <option v-for="option in selectOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn"
            :class="cancelButtonClass"
            @click="close"
            v-if="showCancelButton"
          >
            {{ cancelText }}
          </button>
          <button
            type="button"
            class="btn"
            :class="confirmButtonClass"
            @click="handleConfirm"
            :disabled="isConfirmDisabled"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, nextTick, watch, computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Confirmation'
  },
  message: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'confirm', // 'confirm', 'alert', 'prompt', 'select'
    validator: (value) => ['confirm', 'alert', 'prompt', 'select'].includes(value)
  },
  size: {
    type: String,
    default: 'md', // 'sm', 'md', 'lg', 'xl'
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
  },
  confirmText: {
    type: String,
    default: 'Confirmer'
  },
  cancelText: {
    type: String,
    default: 'Annuler'
  },
  confirmButtonClass: {
    type: String,
    default: 'btn-primary'
  },
  cancelButtonClass: {
    type: String,
    default: 'btn-secondary'
  },
  showCancelButton: {
    type: Boolean,
    default: true
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  inputLabel: {
    type: String,
    default: 'Valeur'
  },
  inputPlaceholder: {
    type: String,
    default: 'Entrez une valeur...'
  },
  inputValue: {
    type: String,
    default: ''
  },
  selectLabel: {
    type: String,
    default: 'Sélectionnez'
  },
  selectOptions: {
    type: Array,
    default: () => []
  },
  selectedValue: {
    type: [String, Number],
    default: ''
  }
});

const emit = defineEmits(['update:show', 'confirm', 'cancel', 'close']);

const inputRef = ref(null);
const selectRef = ref(null);
const id = ref(Math.random().toString(36).substr(2, 9));

// Local reactive variables for v-model
const localInputValue = ref(props.inputValue);
const localSelectedValue = ref(props.selectedValue);

const sizeClass = computed(() => {
  const classes = {
    'sm': 'modal-sm',
    'md': 'modal-md',
    'lg': 'modal-lg',
    'xl': 'modal-xl'
  };
  return classes[props.size] || 'modal-md';
});

const isConfirmDisabled = computed(() => {
  if (props.type === 'prompt') {
    return !props.inputValue.trim();
  }
  if (props.type === 'select') {
    return !props.selectedValue;
  }
  return false;
});

const handleOverlayClick = () => {
  if (props.showCancelButton) {
    close();
  }
};

const handleConfirm = () => {
  let result = true;

  if (props.type === 'prompt') {
    result = localInputValue.value.trim();
  } else if (props.type === 'select') {
    result = localSelectedValue.value;
  }

  emit('confirm', result);
  if (props.type === 'alert') {
    close();
  }
};

const close = () => {
  emit('update:show', false);
  emit('close');
};


// Sync local variables with props
watch(() => props.inputValue, (newVal) => {
  localInputValue.value = newVal;
});

watch(() => props.selectedValue, (newVal) => {
  localSelectedValue.value = newVal;
});

// Focus management
watch(() => props.show, async (newVal) => {
  if (newVal) {
    // Reset local values when modal opens
    localInputValue.value = props.inputValue;
    localSelectedValue.value = props.selectedValue;

    await nextTick();
    if (props.type === 'prompt' && inputRef.value) {
      inputRef.value.focus();
    } else if (props.type === 'select' && selectRef.value) {
      selectRef.value.focus();
    }
  }
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.2s ease-out;
}

.modal-sm { width: 300px; }
.modal-md { width: 500px; }
.modal-lg { width: 700px; }
.modal-xl { width: 900px; }

.modal-header {
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #f8f9fa;
  color: #495057;
}

.modal-body {
  padding: 1.5rem 2rem;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-footer {
  padding: 1rem 2rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
  border-color: #545b62;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #1e7e34;
  border-color: #1e7e34;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #bd2130;
  border-color: #bd2130;
}

.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background-color: #e0a800;
  border-color: #e0a800;
}

.form-control, .form-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus, .form-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-label {
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 576px) {
  .modal-content {
    width: 95vw;
    margin: 1rem;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn {
    width: 100%;
  }
}
</style>